"use client";

import { isPerpMarket, isSpotMarket, Market } from "@decibeltrade/sdk";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useCookieState } from "@/hooks/storage";
import { parseMarketSlug, serializeMarketSlug } from "@/utils/format";
import { updateMarketPageTitle } from "@/utils/title";

import { useAppHealthState } from "./health";
import { LAST_SELECTED_MARKET_COOKIE } from "./last-selected-market-config";
import { useAllMarkets } from "./queries/markets";

function findMarketBySlug(markets: Market[], slug: string) {
  const { marketName, assetType } = parseMarketSlug(slug);
  return markets.find(
    (market) =>
      market.market_name === marketName &&
      (assetType === "spot" ? isSpotMarket(market) : isPerpMarket(market)),
  );
}

const TRADE_SLUG_PATTERN = /^\/trade\/([^?/]*)/;

/**
 * Asset type of the market the trade page points at, resolved from the URL slug
 * (falling back to the last-selected-market cookie) without reading the markets
 * query. Suspense fallbacks need this before `useAllMarkets` resolves — calling
 * `useCurrentMarket` from a fallback would bubble the suspension up to the next
 * boundary.
 */
export function useCurrentMarketAssetType(): "perp" | "spot" {
  const pathname = usePathname();
  const [lastSelectedMarketName] = useCookieState(LAST_SELECTED_MARKET_COOKIE);

  const slug = TRADE_SLUG_PATTERN.exec(pathname)?.[1] || lastSelectedMarketName;
  // With neither a slug nor a cookie, useCurrentMarket settles on markets[0],
  // which is always a perp (spot never sorts to the top of the market list).
  return slug ? parseMarketSlug(slug).assetType : "perp";
}

export function useCurrentMarket() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Extract the market name from the pathname. Unfortunately useParams does not work with shallow routing
  const urlMarketName = TRADE_SLUG_PATTERN.exec(pathname)?.[1];

  const { data: markets } = useAllMarkets();

  const [lastSelectedMarketName, setLastSelectedMarketName] = useCookieState(
    LAST_SELECTED_MARKET_COOKIE,
  );

  const currentMarket = useMemo(() => {
    if (typeof urlMarketName !== "string") {
      return undefined;
    }
    return findMarketBySlug(markets, urlMarketName);
  }, [urlMarketName, markets]);

  // Resolve last selected market from cookie. Available on both server and client,
  // so rendering is consistent and the URL redirect below reflects the stored choice.
  // The cookie stores the serialized slug; legacy values holding a raw market
  // name (e.g. "BTC/USD") parse through the same path as perp markets.
  const lastSelectedMarket = useMemo(() => {
    if (pathname !== "/trade" || urlMarketName || !lastSelectedMarketName) {
      return undefined;
    }
    return findMarketBySlug(markets, lastSelectedMarketName);
  }, [pathname, urlMarketName, lastSelectedMarketName, markets]);

  // Track if we've already triggered a redirect to prevent multiple redirects
  const hasRedirected = useRef(false);

  const setCurrentMarket = useCallback(
    (marketName: string, assetType?: "perp" | "spot") => {
      // Callers without product context (e.g. the footer watchlist, which only
      // has a name) resolve perp-first so pre-spot behavior is unchanged.
      const targetMarket = assetType
        ? markets.find(
            (m) =>
              m.market_name === marketName &&
              (assetType === "spot" ? isSpotMarket(m) : isPerpMarket(m)),
          )
        : (markets.find((m) => m.market_name === marketName && isPerpMarket(m)) ??
          markets.find((m) => m.market_name === marketName));
      const newMarketName = serializeMarketSlug(
        targetMarket ?? { market_name: marketName, asset_type: assetType },
      );
      const newUrl =
        typeof urlMarketName !== "string"
          ? `/trade/${newMarketName}?${searchParams.toString()}`
          : `${pathname.replace(urlMarketName, newMarketName)}?${searchParams.toString()}`;

      if (pathname.includes("/trade")) {
        // window.history.pushState is used to perform a shallow navigation that prevents
        // the RSC (that prefetches data we already have) from being triggered.
        window.history.pushState(null, "", newUrl);
      } else {
        // If the current page is not the trade page, we should do a full navigation to that page
        router.push(newUrl);
      }
    },
    [markets, urlMarketName, searchParams, pathname, router],
  );

  // Reset redirect flag when pathname changes
  useEffect(() => {
    hasRedirected.current = false;
  }, [pathname]);

  useEffect(() => {
    // If the URL contains a market name but the market is not found,
    // delete the unknown market name from the URL.
    if (urlMarketName && !currentMarket) {
      window.history.replaceState(null, "", `/trade?${searchParams.toString()}`);
      return;
    }

    // If we're on /trade without a market, redirect to the last selected market.
    // Uses hasRedirected ref to prevent multiple redirects.
    if (pathname === "/trade" && !urlMarketName && lastSelectedMarket && !hasRedirected.current) {
      hasRedirected.current = true;
      const serializedMarket = serializeMarketSlug(lastSelectedMarket);
      const queryString = searchParams.toString();
      const newUrl = `/trade/${serializedMarket}${queryString ? `?${queryString}` : ""}`;
      window.history.replaceState(null, "", newUrl);
      return;
    }

    if (currentMarket) {
      updateMarketPageTitle(currentMarket.market_name);
      const currentMarketSlug = serializeMarketSlug(currentMarket);
      if (lastSelectedMarketName !== currentMarketSlug) {
        setLastSelectedMarketName(currentMarketSlug);
      }
    }
  }, [
    searchParams,
    pathname,
    currentMarket,
    urlMarketName,
    lastSelectedMarket,
    lastSelectedMarketName,
    setLastSelectedMarketName,
  ]);

  const currentMarketMode = currentMarket?.mode;
  useEffect(() => {
    if (currentMarketMode) {
      useAppHealthState.getState().setCurrentMarketMode(currentMarketMode);
    }
  }, [currentMarketMode]);

  // Priority: URL market > last selected market (cookie) > first market (default)
  const resolvedCurrentMarket = currentMarket ?? lastSelectedMarket ?? markets[0];

  if (!resolvedCurrentMarket) {
    throw new Error("No markets found");
  }

  const currentTokenName = resolvedCurrentMarket.market_name.split("/")[0] ?? "";

  return {
    currentMarket: resolvedCurrentMarket,
    currentTokenName,
    setCurrentMarket,
    markets,
  };
}
